/**
 * FacilitatorConductorPanel — Phase 2
 *
 * Live facilitator command center. Renders as a right-side drawer on the
 * live board (LiveSessionPage). Shows:
 *   - Submission progress bar + avatar roster
 *   - Broadcast announcement input
 *   - Item spotlight picker
 *   - Pause / resume toggle
 *   - The room's timer (S-2): 3, 5 or 10 minutes from the server's clock,
 *     and what happens at zero; T starts three minutes or clears it
 *   - The room's settings for this session (timer at zero, names; S-3)
 *
 * When names are hidden (S-3, ruling R12) the roster shows counts, never a
 * name or initials — the host's screen included.
 *
 * All control actions go through emitSessionEvent so participants see them
 * instantly via Realtime postgres_changes subscriptions.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePresenceStore } from '../../../stores/prioritization/presenceStore';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import { useSessionMessagesStore } from '../../../stores/prioritization/sessionMessagesStore';
import { emitSessionEvent } from '../../../lib/sessionEvents';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { RoomCountdown } from './RoomCountdown';
import { RoomRulesSettings } from '../cycle-config/RoomRulesSettings';
import {
  ROOM_TIMER_CHOICES,
  announceRoomRulesChanged,
  namesHidden,
  setRoomTimer,
  useRoomTimer,
  useSessionRoomRules,
} from '../../../lib/sessions/roomRules';

interface SessionItem {
  id: string;
  name: string;
}

interface FacilitatorConductorPanelProps {
  session: PrioritizationSession;
  toolSessionId: string | null;
  /**
   * The items the room is working on, already named — the same list the tool
   * shows. Spotlight picks from these, so it works for sessions whose items
   * are typed in (Apps) as well as workspace items.
   */
  items: SessionItem[];
  isPaused?: boolean;
  isOpen: boolean;
  onClose: () => void;
  /** Everyone the room expects has submitted (the host bar's count; C07). */
  allIn?: boolean;
  /**
   * Where the drawer's top edge sits, in pixels from the top of the window:
   * the bottom of the host's bar, so Close & save, Board and Join link stay
   * in reach while the controls are open. 0 opens it full height.
   */
  topOffset?: number;
}

function initials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
}

export function FacilitatorConductorPanel({
  session,
  toolSessionId,
  items,
  isPaused = false,
  isOpen,
  onClose,
  allIn = false,
  topOffset = 0,
}: FacilitatorConductorPanelProps) {
  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? '';

  const presentUsers = usePresenceStore(s => s.presentUsers);
  const participantResponses = useToolSessionStore(s => s.participantResponses);
  const sendMessage = useSessionMessagesStore(s => s.sendMessage);

  const [broadcastDraft, setBroadcastDraft] = useState('');
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [spotlightItemId, setSpotlightItemId] = useState<string | null>(null);
  const [isSpotlighting, setIsSpotlighting] = useState(false);

  const { rules, reload: reloadRules } = useSessionRoomRules(session.id);
  const hideNames = namesHidden(rules, 'room');
  const timer = useRoomTimer(session.id, toolSessionId);
  const [timerBusy, setTimerBusy] = useState(false);
  const [timerError, setTimerError] = useState<string | null>(null);
  const workflowId = useDegapProcessStore(s => s.cycles.find(c => c.id === session.cycleId)?.workflowId ?? null);

  const handleTimer = useCallback(async (seconds: number | null) => {
    if (!toolSessionId || timerBusy) return;
    setTimerBusy(true);
    setTimerError(null);
    const result = await setRoomTimer(toolSessionId, seconds);
    setTimerBusy(false);
    if (!result.ok) {
      setTimerError(result.error);
      return;
    }
    timer.setEndsAt(result.timer.hasTimer && result.timer.secondsLeft !== null ? Date.now() + result.timer.secondsLeft * 1000 : null);
  }, [toolSessionId, timerBusy, timer]);

  // T: three minutes, or clear a running timer (P3: the console is keyboard-driven).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey || (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable))) return;
      if (e.key.toLowerCase() !== 't' || !toolSessionId) return;
      e.preventDefault();
      void handleTimer(timer.endsAt !== null && timer.endsAt > Date.now() ? null : ROOM_TIMER_CHOICES[0].seconds);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleTimer, timer.endsAt, toolSessionId]);

  const submittedIds = useMemo(
    () => new Set(
      participantResponses
        .filter(r => r.toolSessionId === toolSessionId && r.isComplete)
        .map(r => r.participantId)
    ),
    [participantResponses, toolSessionId]
  );

  const participantUsers = presentUsers.filter(u => u.role !== 'observer');
  const submitted = submittedIds.size;
  const total = participantUsers.length || (session.participantIds?.length ?? 0);
  const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;

  const handleBroadcast = useCallback(async () => {
    const msg = broadcastDraft.trim();
    if (!msg || isBroadcasting) return;
    setIsBroadcasting(true);
    try {
      const workspaceId = getCurrentWorkspaceId() ?? '';
      // Broadcast via session_messages so chat drawer renders it, AND emit a session_event
      // so announcement listeners can show the banner independently of the chat.
      await sendMessage({ sessionId: session.id, workspaceId, userId, userName, body: `📣 ${msg}` });
      await emitSessionEvent(session.id, userId, userName, 'broadcast_message', { message: msg });
      setBroadcastDraft('');
    } finally {
      setIsBroadcasting(false);
    }
  }, [broadcastDraft, isBroadcasting, sendMessage, session.id, userId, userName]);

  const handleSpotlight = useCallback(async () => {
    if (!spotlightItemId || isSpotlighting) return;
    setIsSpotlighting(true);
    // The name, never the id: participants see this as the item's title.
    const itemTitle = items.find(i => i.id === spotlightItemId)?.name ?? 'Item';
    try {
      await emitSessionEvent(session.id, userId, userName, 'item_spotlighted', {
        itemId: spotlightItemId,
        itemTitle,
      });
    } finally {
      setIsSpotlighting(false);
    }
  }, [spotlightItemId, isSpotlighting, items, session.id, userId, userName]);

  const handleClearSpotlight = useCallback(async () => {
    await emitSessionEvent(session.id, userId, userName, 'item_spotlight_cleared', {});
    setSpotlightItemId(null);
  }, [session.id, userId, userName]);

  const handleTogglePause = useCallback(async () => {
    setIsPausing(true);
    try {
      await emitSessionEvent(
        session.id, userId, userName,
        isPaused ? 'session_resumed' : 'session_paused',
        {}
      );
    } finally {
      setIsPausing(false);
    }
  }, [isPaused, session.id, userId, userName]);

  return (
    <>
      {isOpen && (
        <div
          onClick={onClose}
          style={{ position: 'fixed', top: topOffset, right: 0, bottom: 0, left: 0, zIndex: 100, background: 'rgba(0,0,0,0.2)' }}
          aria-hidden
        />
      )}
      <div
        aria-label="Host controls"
        data-testid="host-controls"
        style={{
          position: 'fixed',
          top: topOffset,
          right: 0,
          bottom: 0,
          width: 360,
          zIndex: 101,
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderLeft: '1px solid #e5e7eb',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: isOpen ? '-4px 0 24px rgba(0,0,0,0.12)' : 'none',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px', height: 52, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>Host controls</div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{session.name}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close the host controls" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1.1rem' }}>×</button>
        </div>

        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Submission progress */}
          <section>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Submissions — {submitted}/{total} ({pct}%)
            </div>
            {allIn && (
              <div data-testid="conductor-all-in" style={{ marginBottom: 8, fontSize: '0.78rem', fontWeight: 600, color: '#065f46', background: '#f0fdf4', border: '1px solid #34d399', borderRadius: 4, padding: '6px 10px' }}>
                All in. Close &amp; save to lock the result, then reveal it.
              </div>
            )}
            <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#34d399' : '#6366f1', borderRadius: 3, transition: 'width 0.4s' }} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {participantUsers.map((u, idx) => {
                const done = submittedIds.has(u.userId);
                return (
                  <div
                    key={`${u.userId}-${idx}`}
                    title={hideNames ? (done ? 'Submitted' : 'Not yet') : `${u.userName}${done ? ' · submitted' : ' · pending'}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '3px 8px', borderRadius: 12,
                      border: `1px solid ${done ? '#34d399' : '#e5e7eb'}`,
                      background: done ? '#f0fdf4' : '#f9fafb',
                      fontSize: '0.72rem', color: done ? '#065f46' : '#374151',
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: done ? '#34d399' : u.isOnline ? '#60a5fa' : '#d1d5db', flexShrink: 0 }} />
                    {!hideNames && initials(u.userName)}
                    {done && <span>✓</span>}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Timer (S-2) */}
          <section data-testid="conductor-timer">
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Timer
            </div>
            {timer.endsAt !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <RoomCountdown
                  endsAt={timer.endsAt}
                  atZeroLabel={timer.atZero === 'lock' ? 'Time is up · answers locked' : 'Time is up'}
                />
                <button
                  type="button"
                  onClick={() => void handleTimer(null)}
                  disabled={timerBusy}
                  style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: '0.75rem', color: '#374151', cursor: 'pointer' }}
                >
                  Clear
                </button>
              </div>
            )}
            {toolSessionId ? (
              <div style={{ display: 'flex', gap: 6 }}>
                {ROOM_TIMER_CHOICES.map(choice => (
                  <button
                    key={choice.seconds}
                    type="button"
                    onClick={() => void handleTimer(choice.seconds)}
                    disabled={timerBusy}
                    style={{ flex: 1, padding: '7px 8px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', color: '#374151', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Start the activity to time it.</div>
            )}
            <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#6b7280' }}>
              {timer.atZero === 'lock'
                ? 'At zero, answers lock: nobody can give or change one.'
                : 'At zero, you are told; answers are still taken.'}{' '}
              Everyone sees the timer. Key: T.
            </div>
            {timerError && <div role="alert" style={{ marginTop: 6, fontSize: '0.72rem', color: '#b91c1c' }}>{timerError}</div>}
          </section>

          {/* This session's room settings (S-2, S-3; inherited from the track, cycle or workflow until set here) */}
          <section data-testid="conductor-room-rules">
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
              Room settings
            </div>
            <div style={{ margin: '0 -16px' }}>
              <RoomRulesSettings
                scopeKind="session"
                scopeId={session.id}
                ids={{
                  workspaceId: session.workspaceId ?? null,
                  workflowId,
                  cycleId: session.cycleId ?? null,
                  trackId: session.trackId ?? null,
                  sessionId: session.id,
                }}
                canEdit
                onSaved={() => {
                  reloadRules();
                  void timer.refresh();
                  void announceRoomRulesChanged(session.id, userId, userName);
                }}
              />
            </div>
          </section>

          {/* Pause / Resume */}
          <section>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Session Control
            </div>
            <button
              type="button"
              onClick={() => void handleTogglePause()}
              disabled={isPausing}
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 6,
                border: `1px solid ${isPaused ? '#f97316' : '#e5e7eb'}`,
                background: isPaused ? '#fff7ed' : '#f9fafb',
                color: isPaused ? '#c2410c' : '#374151',
                fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              {isPaused ? '▶ Resume session' : '⏸ Pause session'}
            </button>
            {isPaused && (
              <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#c2410c', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 4, padding: '6px 10px' }}>
                Participants see a pause overlay and cannot interact with the tool.
              </div>
            )}
          </section>

          {/* Item spotlight */}
          {items.length > 0 && (
            <section>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                Spotlight Item
              </div>
              <select
                value={spotlightItemId ?? ''}
                onChange={e => setSpotlightItemId(e.target.value || null)}
                style={{
                  width: '100%', padding: '7px 10px', borderRadius: 6,
                  border: '1px solid #e5e7eb', background: '#fff',
                  fontSize: '0.82rem', color: '#374151', marginBottom: 8,
                }}
              >
                <option value="">— choose an item —</option>
                {items.map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => void handleSpotlight()}
                  disabled={!spotlightItemId || isSpotlighting}
                  style={{
                    flex: 1, padding: '7px 10px', borderRadius: 6,
                    border: '1px solid #6366f1', background: spotlightItemId ? '#6366f1' : '#f3f4f6',
                    color: spotlightItemId ? '#fff' : '#9ca3af',
                    fontSize: '0.78rem', fontWeight: 600, cursor: spotlightItemId ? 'pointer' : 'default',
                  }}
                >
                  ✦ Spotlight
                </button>
                <button
                  type="button"
                  onClick={() => void handleClearSpotlight()}
                  style={{
                    padding: '7px 10px', borderRadius: 6,
                    border: '1px solid #e5e7eb', background: '#f9fafb',
                    color: '#6b7280', fontSize: '0.78rem', cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              </div>
            </section>
          )}

          {/* Broadcast */}
          <section>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Broadcast to All
            </div>
            <textarea
              value={broadcastDraft}
              onChange={e => setBroadcastDraft(e.target.value)}
              placeholder="A message visible to all participants as a banner…"
              rows={3}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 6,
                border: '1px solid #e5e7eb', background: '#fff',
                fontSize: '0.82rem', color: '#374151', resize: 'vertical',
                fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
            <button
              type="button"
              onClick={() => void handleBroadcast()}
              disabled={!broadcastDraft.trim() || isBroadcasting}
              style={{
                marginTop: 6, width: '100%', padding: '8px', borderRadius: 6,
                border: '1px solid #f59e0b',
                background: broadcastDraft.trim() ? '#f59e0b' : '#f9fafb',
                color: broadcastDraft.trim() ? '#fff' : '#9ca3af',
                fontSize: '0.82rem', fontWeight: 600,
                cursor: broadcastDraft.trim() ? 'pointer' : 'default',
              }}
            >
              📣 Broadcast
            </button>
          </section>

          {/* The Board (ruling R5: the Board replaces TV mode) — the same page as the bar's Board button */}
          <section>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Shared Display
            </div>
            <a
              href={`/prioritization/sessions/${session.id}/tv`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'block', width: '100%', padding: '8px 12px', borderRadius: 6,
                border: '1px solid #e5e7eb', background: '#f9fafb',
                color: '#374151', fontSize: '0.82rem', fontWeight: 600,
                textDecoration: 'none', textAlign: 'center',
              }}
            >
              📺 Open the Board
            </a>
            <div style={{ marginTop: 4, fontSize: '0.7rem', color: '#9ca3af' }}>
              The shared display for the front of the room, in a new tab: who is in, the code to join, the reveal.
            </div>
          </section>

          {/* Roster */}
          <section>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Who's in the room ({presentUsers.length})
            </div>
            {hideNames ? (
              <div style={{ fontSize: '0.78rem', color: '#374151' }} data-testid="conductor-roster-counts">
                {participantUsers.length} {participantUsers.length === 1 ? 'person' : 'people'} answering · {submitted} submitted. Names are hidden in this session.
              </div>
            ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* One person can be here twice (e.g. their own TV tab joins as an observer). */}
              {presentUsers.map((u, idx) => (
                <div
                  key={`${u.userId}-${idx}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 4, background: '#f9fafb' }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: u.isOnline ? '#34d399' : '#d1d5db', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: '0.78rem', color: '#374151' }}>{u.userName}</span>
                  <span style={{ fontSize: '0.62rem', color: '#9ca3af', textTransform: 'capitalize' }}>{u.role}</span>
                  {u.currentActivity && (
                    <span style={{ fontSize: '0.58rem', color: '#6366f1', background: '#eef2ff', borderRadius: 3, padding: '1px 5px' }}>
                      {u.currentActivity}
                    </span>
                  )}
                </div>
              ))}
              {presentUsers.length === 0 && (
                <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>No one connected yet</div>
              )}
            </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
